/**
 * Which school an address names.
 *
 * This was three copies of the same twelve lines -- App, Login and Layout each had their own -- and
 * they had to agree, because a disagreement means the login screen brands itself as one school
 * while the session resolves to another. One copy, imported three times.
 *
 * A school is reached at its own address and nowhere else. `?tenant=` is honoured on localhost
 * only: development has no subdomains, so the label has to travel somehow, but in production the
 * parameter would be a second way in that bypasses the address entirely -- exactly the thing the
 * bare domain no longer does either.
 */

// Not schools. `admin-portal-d` is the Firebase host itself, so the bare product domain is
// `admin-portal-d.web.app` and its first label must not be read as a tenant.
const RESERVED = ['www', 'admin-portal-d', 'admin'];

const isDevHost = (hostname) => hostname === 'localhost' || hostname === '127.0.0.1';

/**
 * A school's `domain` field, tidied: the address itself, not a fragment of it.
 *
 * `hsss.d-admin.com` is the school's actual subdomain and is what belongs in that field -- somebody
 * reading the Developer Portal should see the address they would type. This only removes what is
 * not part of a host: a pasted protocol, a path, stray case and spacing.
 */
export const normaliseHost = (raw) => String(raw || '')
  .trim()
  .toLowerCase()
  .replace(/^https?:\/\//, '')
  .replace(/[/?#].*$/, '')
  .replace(/\.+$/, '')
  .replace(/[^a-z0-9.-]/g, '');

/**
 * What that address is keyed by: its first label.
 *
 * Not a lesser version of the host -- a different thing. One school answers at more than one
 * address (`hsss.d-admin.com` and `hsss.admin-portal-d.web.app` are the same school), and both have
 * to reach one entry. The label is what they share, and it is what tenantFromHost() hands back from
 * whichever address a visitor actually used.
 */
export const normaliseLabel = (raw) => normaliseHost(raw).split('.')[0].replace(/[^a-z0-9-]/g, '');

/** The subdomain label, or null when the host names no school. */
export const tenantFromHost = (hostname) => {
  const host = String(hostname || '');
  const parts = host.split('.');
  // A bare `example.com` has two parts and no room for a subdomain; `x.localhost` has two but does.
  if (!(parts.length > 2 || (parts.length > 1 && host.includes('localhost')))) return null;
  const label = parts[0];
  return RESERVED.includes(label) ? null : label;
};

export const tenantFromLocation = ({ hostname, search } = window.location) => {
  if (isDevHost(hostname)) {
    const param = new URLSearchParams(search || '').get('tenant');
    if (param) return param;
  }
  return tenantFromHost(hostname);
};
